import React from 'react';
import { motion } from 'framer-motion';
import { fadeIn, fadeInUp } from '@/motion/variants';
import { TRUST_BAR_REMOTE_LOGOS } from '@/lib/constants';

/**
 * Business logic: Trust bar with recognizable partner logos.
 * Goal is to provide immediate social proof to increase perceived reliability.
 */

interface Partner {
  name: string;
  logoSrc: string;
  alt: string;
  url: string;
}

interface TrustBarProps {
  title?: string;
  partners: Partner[];
  variant?: 'carousel' | 'grid';
  grayscale?: boolean;
  wrap?: boolean;
}

// Partner data with local logo files from /public/partners
const partnerData = {
  insurance: [
    { name: 'AXA', logoSrc: '/partners/AXA_Logo.png', alt: 'AXA', url: 'https://www.axa.com' },
    { name: 'Chubb', logoSrc: '/partners/Chubb-Limited-logo.png', alt: 'Chubb Limited', url: 'https://www.chubb.com' },
    { name: 'Allianz', logoSrc: '/partners/Allianz_logo.png', alt: 'Allianz', url: 'https://www.allianz.com' }
  ],
  shipping: [
    { name: 'MSC', logoSrc: '/partners/Mediterranean_Shipping_Company_logo.png', alt: 'Mediterranean Shipping Company', url: 'https://www.msc.com' },
    { name: 'Maersk', logoSrc: '/partners/Maersk_Group_Logo.png', alt: 'Maersk Group', url: 'https://www.maersk.com' },
    { name: 'COSCO Shipping', logoSrc: '/partners/COSCO_Shipping_Logo.png', alt: 'COSCO Shipping', url: 'https://www.coscoshipping.com' },
    { name: 'CMA CGM', logoSrc: '/partners/CMA_CGM_logo.png', alt: 'CMA CGM', url: 'https://www.cma-cgm.com' },
    { name: 'Cotransa', logoSrc: '/partners/contransa-logo.svg', alt: 'Cotransa', url: 'https://www.contransa.com' }
  ],
  inspection: [
    { name: 'Intertek', logoSrc: '/partners/Logo_Intertek_01.png', alt: 'Intertek', url: 'https://www.intertek.com' },
    { name: 'Bureau Veritas', logoSrc: '/partners/Bureau_Veritas_Logo.jpg', alt: 'Bureau Veritas', url: 'https://www.bureauveritas.com' },
    { name: 'SGS', logoSrc: '/partners/SGS_Logo.png', alt: 'SGS', url: 'https://www.sgs.com' }
  ],
  banking: [
    { name: 'SWIFT', logoSrc: '/partners/SWIFT.png', alt: 'SWIFT', url: 'https://www.swift.com' },
    { name: 'Anext', logoSrc: '/partners/anext.gif', alt: 'Anext', url: 'https://www.anext.com' },
    { name: 'BACB', logoSrc: '/partners/BACB_idvT3E0_R6_0.svg', alt: 'BACB', url: 'https://www.bacb.com' }
  ],
  cloud: [
    { name: 'Cloudflare', logoSrc: TRUST_BAR_REMOTE_LOGOS.cloudflare, alt: 'Cloudflare', url: 'https://www.cloudflare.com' },
    { name: 'AWS', logoSrc: TRUST_BAR_REMOTE_LOGOS.aws, alt: 'Amazon Web Services', url: 'https://aws.amazon.com' },
    { name: 'DigitalOcean', logoSrc: TRUST_BAR_REMOTE_LOGOS.digitalOcean, alt: 'DigitalOcean', url: 'https://www.digitalocean.com' },
    { name: 'Vultr', logoSrc: TRUST_BAR_REMOTE_LOGOS.vultr, alt: 'Vultr', url: 'https://www.vultr.com' }
  ],
  openai: [
    { name: 'OpenAI', logoSrc: TRUST_BAR_REMOTE_LOGOS.openai, alt: 'OpenAI', url: 'https://www.openai.com' }
  ],
  consulting: [
    { name: 'Antler', logoSrc: TRUST_BAR_REMOTE_LOGOS.antler, alt: 'Antler', url: 'https://www.antler.co' },
    { name: 'Eon Protocol', logoSrc: 'https://media.licdn.com/dms/image/v2/D560BAQFyhXMddz3dIA/company-logo_200_200/B56ZUC3P7lHQAM-/0/1739509793251/eon_protocol_logo?e=1760572800&v=beta&t=8xBiGSPaEKL-04JEkxUmv1i4Wgj7wk04Rj1a7GdszDQ', alt: 'Eon Protocol', url: 'https://www.eonprotocol.com' },
    { name: 'Valven Studio', logoSrc: TRUST_BAR_REMOTE_LOGOS.valvenStudio, alt: 'Valven Studio', url: 'https://valvenstudio.com' },
    { name: 'Entain Group', logoSrc: TRUST_BAR_REMOTE_LOGOS.entain, alt: 'Entain Group', url: 'https://www.entaingroup.com' },
    { name: 'Comcast', logoSrc: TRUST_BAR_REMOTE_LOGOS.comcast, alt: 'Comcast', url: 'https://www.comcast.com' },
    { name: 'Data Science for Asia', logoSrc: 'https://media.licdn.com/dms/image/v2/D560BAQFhrS8I1Ry9Kw/company-logo_200_200/B56ZbsxgB0GsAI-/0/1747729122142/dsiasia_logo?e=1760572800&v=beta&t=VAxdmb_aSqeqDNEY--2KMoNeO5Qoa-ByQOwniZtTH0g', alt: 'Data Science for Asia', url: 'https://dsiasia.io' }
  ]
};

// Predefined TrustBar components for different contexts
export const AllPartnersTrustBar = () => {
  const allPartners = [
    ...partnerData.insurance,
    ...partnerData.shipping,
    ...partnerData.inspection,
    ...partnerData.banking,
    ...partnerData.cloud,
    ...partnerData.openai
  ];
  return <TrustBar title="Trusted Partners" partners={allPartners} variant="carousel" grayscale={false} />;
};

export const TradingPartnersTrustBar = () => {
  const tradingPartners = [
    ...partnerData.insurance,
    ...partnerData.shipping,
    ...partnerData.inspection,
    ...partnerData.banking
  ];
  return <TrustBar title="Trusted Partners" partners={tradingPartners} variant="carousel" grayscale={false} />;
};

export const CloudPartnersTrustBar = () => {
  const cloudPartners = [
    ...partnerData.cloud,
    ...partnerData.openai
  ];

  return <TrustBar title="Trusted Partners" partners={cloudPartners} variant="carousel" grayscale={false} />;
};

export const ConsultingPartnersTrustBar = () => {
  return <TrustBar title="Previous Collaborations" partners={partnerData.consulting} variant="carousel" grayscale={false} />;
};

// Legacy components for backward compatibility
export const ShippingTrustBar = () => (
  <TrustBar title="Shipping Partners" partners={partnerData.shipping} variant="grid" />
);

export const BankingTrustBar = () => (
  <TrustBar title="Banking & Payment Partners" partners={partnerData.banking} variant="grid" />
);

export const InsuranceTrustBar = () => (
  <TrustBar title="Insurance Partners" partners={partnerData.insurance} variant="grid" />
);

export const InspectionTrustBar = () => (
  <TrustBar title="Inspection & Certification Partners" partners={partnerData.inspection} variant="grid" />
);

const TrustBar: React.FC<TrustBarProps> = ({
  title = "Trusted Partners",
  partners,
  variant = 'grid',
  grayscale = true,
  wrap = true
}) => {
  // Debug: Check if partners is undefined
  if (!partners) {
    console.error('TrustBar: partners prop is undefined');
    return null;
  }

  if (variant === 'carousel') {
    // Original carousel implementation for cloud partners
    const track = [...partners, ...partners, ...partners];
    return (
      <motion.section
        variants={fadeInUp}
        initial="initial"
        animate="animate"
        className="py-2 overflow-hidden"
      >
        <div className="mx-auto px-16 md:px-24">
          <div className="text-center mb-4">
            <p className="text-md uppercase tracking-wider text-muted-foreground font-medium">{title}</p>
          </div>

          <div className="relative overflow-hidden">
            <div className="marquee-ltr flex items-center gap-6 justify-center" aria-label={`${title} carousel`}>
              {track.map((partner, idx) => (
                <motion.a
                  key={`${partner.name}-${idx}`}
                  href={partner.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 shrink-0 group"
                  whileHover={{ scale: 1.08 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="w-16 h-16 rounded-xl bg-card border border-border overflow-hidden flex items-center justify-center shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                    <img
                      src={partner.logoSrc}
                      alt={partner.alt}
                      className={`w-12 h-12 object-contain ${grayscale ? 'grayscale hover:grayscale-0' : ''} transition-all duration-300`}
                      loading="lazy"
                      decoding="async"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/placeholder.svg';
                        target.style.display = 'block';
                        if (import.meta.env.DEV) {
                          console.warn(`[TrustBar] Failed to load logo for ${partner.name}`);
                        }
                      }}
                    />
                  </div>
                  <span className="text-xs text-secondary hidden md:inline whitespace-nowrap">{partner.name}</span>
                </motion.a>
              ))}
            </div>
          </div>
        </div>
      </motion.section>
    );
  }

  // Grid layout for other partner types
  return (
    <motion.section
      variants={fadeInUp}
      initial="initial"
      animate="animate"
      className="py-8"
    >
      <div className="container mx-auto px-6">
        {title && (
          <div className="text-center mb-6">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{title}</p>
          </div>
        )}

        <div className={`grid gap-4 ${wrap ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-3 md:grid-cols-6'} justify-items-center max-w-4xl mx-auto`}>
          {partners.map((partner, idx) => (
            <motion.a
              key={`${partner.name}-${idx}`}
              href={partner.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 group"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <div className="w-20 h-20 rounded-lg bg-card border border-border overflow-hidden flex items-center justify-center shadow-sm group-hover:shadow-md transition-all duration-300 cursor-pointer">
                <img
                  src={partner.logoSrc}
                  alt={partner.alt}
                  className={`w-16 h-16 object-contain ${grayscale ? 'grayscale group-hover:grayscale-0' : ''} transition-all duration-300`}
                  loading="lazy"
                  decoding="async"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/placeholder.svg';
                    target.style.display = 'block';
                    if (import.meta.env.DEV) {
                      console.warn(`[TrustBar] Failed to load logo for ${partner.name}`);
                    }
                  }}
                />
              </div>
              <span className="text-xs text-center text-muted-foreground group-hover:text-foreground transition-colors">
                {partner.name}
              </span>
            </motion.a>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

export default TrustBar;

